import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMedal, FaTrophy, FaStar, FaSearch, FaCertificate, FaPlus, FaTimes, FaUser, FaEnvelope, FaPhone, FaGlobe, FaBookReader } from 'react-icons/fa';
import { RocketOutlined, TeamOutlined, ExperimentOutlined, ThunderboltOutlined } from '@ant-design/icons';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { collection, query, getDocs, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, auth } from '../../../../auth/firebase';
import './user_honors.css';
import { CERTIFICATES, formatCertificateDate, checkCertificateEligibility, generateCertificatePDF } from './usr_mng_utils/user_cert';
import './usr_mng_utils/user_cert.css';

// # Import badge images
import rocketBadge from '../../../../aneo_media/profile/badges/rocket.png';
import teamPlayerBadge from '../../../../aneo_media/profile/badges/teamplayer.png';
import scientistBadge from '../../../../aneo_media/profile/badges/scientist.png';
import jetBadge from '../../../../aneo_media/profile/badges/jet.png';

// # Badge definitions
const BADGES = {
  FIGHTER: {
    id: 'fighter',
    name: 'Fighter',
    description: 'Scoring High on assessments after scoring low initially',
    icon: jetBadge,
    color: '#E5E4E2', // Platinum
    type: 'platinum'
  },
  TEAM_PLAYER: {
    id: 'team_player',
    name: 'Team Player',
    description: 'Compliant and consistent',
    icon: teamPlayerBadge,
    color: '#FFD700', // Golden
    type: 'golden'
  },
  SCIENTIST: {
    id: 'scientist',
    name: 'Scientist',
    description: 'Thoughtful and imaginative',
    icon: scientistBadge,
    color: '#FFD700', // Golden
    type: 'golden'
  },
  ADRENALINE_JUNKY: {
    id: 'adrenaline_junky',
    name: 'Adrenaline Junky',
    description: 'Faster than a rocket when the situation requires',
    icon: rocketBadge,
    color: '#C0C0C0', // Silver
    type: 'silver'
  }
};

// # Main UserHonors component
const UserHonors = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('badges');
  const [badgeSearch, setBadgeSearch] = useState('');
  const [certificateSearch, setCertificateSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  
  // # Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBadgeSelector, setShowBadgeSelector] = useState(false);
  const [showCertificateSelector, setShowCertificateSelector] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [isAssigningBadge, setIsAssigningBadge] = useState(false);
  const [isAssigningCertificate, setIsAssigningCertificate] = useState(false);

  // # Handle user selection for modal
  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowBadgeSelector(true);
    setSelectedBadge(null);
  };

  // # Handle badge deletion
  const handleDeleteBadge = async (badgeToDelete) => {
    try {
      // # Update user's badges in Firestore
      const userRef = doc(db, 'users', selectedUser.uid);
      const updatedBadges = selectedUser.badges.filter(
        badge => badge.id !== badgeToDelete.id
      );
      
      await updateDoc(userRef, {
        my_badges: updatedBadges
      });

      // # Update local state
      setUsers(users.map(user => {
        if (user.uid === selectedUser.uid) {
          return {
            ...user,
            badges: updatedBadges
          };
        }
        return user;
      }));

      // # Update selected user state
      setSelectedUser(prev => ({
        ...prev,
        badges: updatedBadges
      }));

    } catch (error) {
      console.error('Error deleting badge:', error);
      setError('Failed to delete badge. Please try again.');
    }
  };

  // # Close modal
  const handleCloseModal = () => {
    setSelectedUser(null);
    setShowBadgeSelector(false);
    setSelectedBadge(null);
  };

  // # Handle badge selection
  const handleBadgeSelect = (badge) => {
    setSelectedBadge(badge);
  };

  // # Handle badge assignment
  const handleAssignBadge = async () => {
    if (!selectedUser || !selectedBadge) return;

    try {
      // # Check if user already has this badge
      const hasBadge = selectedUser.badges.some(badge => badge.id === selectedBadge.id);
      
      if (hasBadge) {
        setError('User already has this badge');
        return;
      }

      setIsAssigningBadge(true);

      // # Get current admin info
      const currentAdmin = auth.currentUser;
      if (!currentAdmin) {
        setError('Admin authentication required');
        return;
      }
      
      // # Create badge object with assignment details
      const badgeToAssign = {
        id: selectedBadge.id,
        name: selectedBadge.name,
        description: selectedBadge.description,
        icon: selectedBadge.icon,
        color: selectedBadge.color,
        type: selectedBadge.type,
        assignedAt: new Date().toISOString(),
        assignedBy: {
          uid: currentAdmin.uid,
          role: "admin",
          timestamp: new Date().toISOString()
        }
      };
      
      // # Update user's my_badges array in Firestore
      const userRef = doc(db, 'users', selectedUser.uid);
      await updateDoc(userRef, {
        my_badges: arrayUnion(badgeToAssign)
      });

      // # Update local state
      setUsers(users.map(user => {
        if (user.uid === selectedUser.uid) {
          return {
            ...user,
            badges: [...user.badges, badgeToAssign]
          };
        }
        return user;
      }));

      // # Reset states
      setShowBadgeSelector(false);
      setSelectedBadge(null);
      setSelectedUser(null);
      setError(null);
    } catch (error) {
      console.error('Error assigning badge:', error);
      setError('Failed to assign badge. Please try again.');
    } finally {
      setIsAssigningBadge(false);
    }
  };

  // # Handle give badge button click
  const handleGiveBadge = (userId) => {
    const user = users.find(u => u.uid === userId);
    if (user) {
      setSelectedUser(user);
      setShowBadgeSelector(true);
    }
  };

  // # Handle certificate selection
  const handleCertificateSelect = (certificate) => {
    setSelectedCertificate(certificate);
  };

  // # Handle certificate assignment
  const handleAssignCertificate = async () => {
    if (!selectedUser || !selectedCertificate) return;

    try {
      // # Check if user already has this certificate
      const hasCertificate = selectedUser.certificates.some(
        cert => cert.type === selectedCertificate.type
      );
      
      if (hasCertificate) {
        setError('User already has this certificate');
        return;
      }

      setIsAssigningCertificate(true);

      // # Get current admin info
      const currentAdmin = auth.currentUser;
      if (!currentAdmin) {
        setError('Admin authentication required');
        return;
      }
      
      // # Create certificate object with assignment details
      const certificateToAssign = {
        id: selectedCertificate.id,
        name: selectedCertificate.name,
        description: selectedCertificate.description,
        type: selectedCertificate.type,
        issueDate: new Date().toISOString(),
        issuedBy: {
          uid: currentAdmin.uid,
          role: "admin",
          timestamp: new Date().toISOString()
        }
      };
      
      // # Update user's certificates array in Firestore
      const userRef = doc(db, 'users', selectedUser.uid);
      await updateDoc(userRef, {
        certificates: arrayUnion(certificateToAssign)
      });

      // # Update local state
      setUsers(users.map(user => {
        if (user.uid === selectedUser.uid) {
          return {
            ...user,
            certificates: [...user.certificates, certificateToAssign]
          };
        }
        return user;
      }));

      // # Reset states
      setShowCertificateSelector(false);
      setSelectedCertificate(null);
      setSelectedUser(null);
      setError(null);
    } catch (error) {
      console.error('Error assigning certificate:', error);
      setError('Failed to assign certificate. Please try again.');
    } finally {
      setIsAssigningCertificate(false);
    }
  };

  // # Handle certificate deletion
  const handleDeleteCertificate = async (certificateToDelete) => {
    try {
      // # Update user's certificates in Firestore
      const userRef = doc(db, 'users', selectedUser.uid);
      const updatedCertificates = selectedUser.certificates.filter(
        cert => cert.id !== certificateToDelete.id
      );
      
      await updateDoc(userRef, {
        certificates: updatedCertificates
      });

      // # Update local state
      setUsers(users.map(user => {
        if (user.uid === selectedUser.uid) {
          return {
            ...user,
            certificates: updatedCertificates
          };
        }
        return user;
      }));

      // # Update selected user state
      setSelectedUser(prev => ({
        ...prev,
        certificates: updatedCertificates
      }));

    } catch (error) {
      console.error('Error deleting certificate:', error);
      setError('Failed to delete certificate. Please try again.');
    }
  };

  // # Handle user click for certificates
  const handleUserCertificateClick = (user) => {
    setSelectedUser(user);
    setShowCertificateSelector(true);
    setSelectedCertificate(null);
  };

  // # Handle give certificate button click
  const handleGiveCertificate = (userId) => {
    const user = users.find(u => u.uid === userId);
    if (user) {
      setSelectedUser(user);
      setShowCertificateSelector(true);
    }
  };

  // # Fetch users data
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // # Get users collection
        const usersQuery = query(collection(db, 'users'));
        const usersSnapshot = await getDocs(usersQuery);
        
        // # Get subscriptions collection
        const subsQuery = query(collection(db, 'subscriptions'));
        const subsSnapshot = await getDocs(subsQuery);
        
        // # Create subscription map
        const subscriptionMap = {};
        subsSnapshot.forEach((doc) => {
          subscriptionMap[doc.id] = doc.data();
        });

        // # Process user data
        const userData = usersSnapshot.docs.map(doc => {
          const user = doc.data();
          const subscription = subscriptionMap[doc.id];
          
          // # Count active subscriptions
          const activeSubscriptions = subscription ? 
            Object.values(subscription.subscriptions || {})
              .filter(sub => sub.status === 'active').length : 0;

          // # Get badges from my_badges field
          const badges = user.my_badges || [];

          return {
            uid: doc.id,
            email: user.email || 'No email',
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User',
            photoURL: user.photoURL || null,
            phoneNumber: user.phoneNumber || 'N/A',
            selectedCountry: user.selectedCountry || { code: 'N/A' },
            activeSubscriptions,
            badges, // # Use my_badges array
            certificates: user.certificates || []
          };
        });

        setUsers(userData);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to load users data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // # Filter users based on search
  const filteredUsers = users.filter(user => {
    const searchTerm = activeTab === 'badges' ? badgeSearch : certificateSearch;
    const searchLower = searchTerm.toLowerCase();
    
    return (
      user.fullName.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      user.phoneNumber.toLowerCase().includes(searchLower)
    );
  });

  // # Display error message in modal if badge assignment fails
  const renderError = () => {
    if (!error) return null;
    
    return (
      <div className="honors_modal_error">
        <p>{error}</p>
        <button onClick={() => setError(null)}>Dismiss</button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="honors_container">
        <SharedHeader title="User Honors" />
        <div className="honors_loading">Loading...</div>
        <DashboardFooter />
      </div>
    );
  }

  return (
    <div className="honors_container">
      <SharedHeader title="User Honors" />
      
      <div className="honors_content">
        {/* Title Box */}
        <div className="honors_title_box">
          <div className="honors_title_content">
            <FaMedal className="honors_title_icon" />
            <div className="honors_title_text">
              <h1>User Honors System</h1>
              <p>Manage and track user achievements and certifications</p>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="honors_tabs">
          <button 
            className={`honors_tab ${activeTab === 'badges' ? 'active' : ''}`}
            onClick={() => setActiveTab('badges')}
          >
            <FaTrophy className="honors_tab_icon" />
            User Badges
          </button>
          <button 
            className={`honors_tab ${activeTab === 'certificates' ? 'active' : ''}`}
            onClick={() => setActiveTab('certificates')}
          >
            <FaCertificate className="honors_tab_icon" />
            User Certificates
          </button>
        </div>

        {/* Search and Table Area */}
        <div className="honors_tab_content">
          <div className="honors_section">
            <div className="honors_search_container">
              <FaSearch className="honors_search_icon" />
              <input
                type="text"
                placeholder={`Search ${activeTab === 'badges' ? 'badges' : 'certificates'}...`}
                value={activeTab === 'badges' ? badgeSearch : certificateSearch}
                onChange={(e) => activeTab === 'badges' ? 
                  setBadgeSearch(e.target.value) : 
                  setCertificateSearch(e.target.value)}
                className="honors_search_input"
              />
            </div>

            {error ? (
              <div className="honors_error">
                <p>{error}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
              </div>
            ) : (
              <div className="honors_table_container">
                <table className="honors_table">
                  <thead>
                    <tr>
                      <th>Profile</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Contact</th>
                      <th>Active Subscriptions</th>
                      <th>{activeTab === 'badges' ? 'Badges' : 'Certificates'}</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.uid}>
                        <td>
                          <div 
                            className="honors_user_avatar clickable"
                            onClick={() => handleUserClick(user)}
                          >
                            <img 
                              src={user.photoURL || '/icons/pfp.png'} 
                              alt={user.fullName}
                            />
                          </div>
                        </td>
                        <td>{user.fullName}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className="honors_contact">
                            {user.selectedCountry.code} {user.phoneNumber}
                          </span>
                        </td>
                        <td>
                          <span className="honors_subscription_count">
                            {user.activeSubscriptions}
                          </span>
                        </td>
                        <td>
                          <span className="honors_count">
                            {activeTab === 'badges' ? 
                              user.badges.length : 
                              user.certificates.length}
                          </span>
                        </td>
                        <td>
                          <button
                            className="honors_action_button"
                            onClick={() => activeTab === 'badges' ? 
                              handleGiveBadge(user.uid) : 
                              handleGiveCertificate(user.uid)}
                          >
                            <FaPlus />
                            {activeTab === 'badges' ? 'Give Badge' : 'Certify'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <DashboardFooter />

      {/* # User Details Modal */}
      {selectedUser && (
        <div className="honors_modal_overlay" onClick={handleCloseModal}>
          <div className="honors_modal" onClick={e => e.stopPropagation()}>
            <button className="honors_modal_close" onClick={handleCloseModal}>
              <FaTimes />
            </button>

            <div className="honors_modal_content">
              {/* # User Profile Section */}
              <div className="honors_modal_profile">
                <div className="honors_modal_avatar">
                  <img src={selectedUser.photoURL || '/icons/pfp.png'} alt={selectedUser.fullName} />
                </div>
                <h2>{selectedUser.fullName}</h2>
                <p className="honors_modal_email">{selectedUser.email}</p>
              </div>

              {/* # User Details Grid */}
              <div className="honors_modal_grid">
                {/* # Personal Information */}
                <div className="honors_modal_section">
                  <h3><FaUser /> Personal Information</h3>
                  <div className="honors_modal_info">
                    <div className="honors_info_item">
                      <label>Email</label>
                      <p><FaEnvelope /> {selectedUser.email}</p>
                    </div>
                    <div className="honors_info_item">
                      <label>Phone</label>
                      <p><FaPhone /> {selectedUser.phoneNumber}</p>
                    </div>
                    <div className="honors_info_item">
                      <label>Country</label>
                      <p><FaGlobe /> {selectedUser.selectedCountry.code}</p>
                    </div>
                  </div>
                </div>

                {/* # Academic Information */}
                <div className="honors_modal_section">
                  <h3><FaBookReader /> Academic Information</h3>
                  <div className="honors_modal_info">
                    <div className="honors_info_item">
                      <label>University</label>
                      <p>{selectedUser.university || 'Not specified'}</p>
                    </div>
                    <div className="honors_info_item">
                      <label>Study Phase</label>
                      <p>{selectedUser.studyPhase || 'Not specified'}</p>
                    </div>
                    <div className="honors_info_item">
                      <label>Active Subscriptions</label>
                      <p>{selectedUser.activeSubscriptions}</p>
                    </div>
                  </div>
                </div>

                {/* # Current Badges/Certificates Section */}
                <div className="honors_modal_section">
                  <h3>
                    {activeTab === 'badges' ? (
                      <><FaMedal /> Current Badges</>
                    ) : (
                      <><FaCertificate /> Current Certificates</>
                    )}
                  </h3>
                  {activeTab === 'badges' ? (
                    <div className="honors_badges_grid current_badges">
                      {selectedUser.badges.map((badge, index) => (
                        <div key={`${badge.id}-${index}`} className="honors_badge_item">
                          <button 
                            className="honors_badge_delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteBadge(badge);
                            }}
                          >
                            <FaTimes />
                          </button>
                          <img src={badge.icon} alt={badge.name} />
                          <span className="honors_badge_type">{badge.type}</span>
                        </div>
                      ))}
                      {selectedUser.badges.length === 0 && (
                        <p className="honors_no_badges">No badges earned yet</p>
                      )}
                    </div>
                  ) : (
                    <div className="cert-section">
                      <h3 className="cert_section_title">Current Certificates</h3>
                      {selectedUser?.certificates?.length > 0 ? (
                        selectedUser.certificates.map((cert) => (
                          <div key={cert.id} className="cert_display">
                            <div className="cert_display_icon">
                              <i className="fas fa-certificate"></i>
                            </div>
                            <div className="cert_display_info">
                              <h4 className="cert_display_name">{cert.name}</h4>
                              <p className="cert_display_date">
                                Awarded on {formatCertificateDate(cert.issueDate)}
                              </p>
                            </div>
                            <button 
                              className="cert_preview_btn"
                              onClick={() => generateCertificatePDF(selectedUser, cert)}
                            >
                              <i className="fas fa-download"></i>
                              Download PDF
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="no-certs-message">No certificates awarded yet.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* # Badge/Certificate Selector */}
              {(showBadgeSelector || showCertificateSelector) && (
                <div className={activeTab === 'badges' ? "honors_badge_selector" : "cert_selector_container"}>
                  <div className="cert_selector_header">
                    <h3>Select {activeTab === 'badges' ? 'Badge' : 'Certificate'} to Award</h3>
                    {renderError()}
                  </div>
                  
                  {activeTab === 'badges' ? (
                    <div className="honors_badges_grid">
                      {Object.values(BADGES).map(badge => (
                        <div
                          key={badge.id}
                          className={`honors_badge_option ${selectedBadge?.id === badge.id ? 'selected' : ''}`}
                          onClick={() => handleBadgeSelect(badge)}
                          style={{ backgroundColor: badge.color }}
                        >
                          <div className="honors_badge_icon">
                            <img src={badge.icon} alt={badge.name} />
                          </div>
                          <div className="honors_badge_info">
                            <h1 className="honors_badge_name">{badge.name}</h1>
                            <span className="honors_badge_type">{badge.type}</span>
                            <p className="honors_badge_description">{badge.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="cert_grid">
                      {Object.values(CERTIFICATES).map(cert => (
                        <div
                          key={cert.id}
                          className={`cert_option ${selectedCertificate?.id === cert.id ? 'selected' : ''}`}
                          onClick={() => handleCertificateSelect(cert)}
                        >
                          <div className="cert_icon">
                            <i className="fas fa-award"></i>
                          </div>
                          <h3 className="cert_name">{cert.name}</h3>
                          <p className="cert_description">{cert.description}</p>
                          <button 
                            className="cert_preview_btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              generateCertificatePDF(selectedUser, cert);
                            }}
                          >
                            <i className="fas fa-download"></i>
                            Download PDF
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="honors_modal_actions">
                    <button 
                      className="honors_cancel_button"
                      onClick={() => activeTab === 'badges' ? setShowBadgeSelector(false) : setShowCertificateSelector(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="honors_assign_button"
                      onClick={activeTab === 'badges' ? handleAssignBadge : handleAssignCertificate}
                      disabled={
                        activeTab === 'badges' 
                          ? (!selectedBadge || isAssigningBadge)
                          : (!selectedCertificate || isAssigningCertificate)
                      }
                    >
                      {activeTab === 'badges' 
                        ? (isAssigningBadge ? 'Assigning...' : 'Assign Badge')
                        : (isAssigningCertificate ? 'Assigning...' : 'Award Certificate')
                      }
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserHonors;
